import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { SalaryPinCard } from './SalaryPinCard';
import { ConfirmPaymentDialog } from './ConfirmPaymentDialog';
import { ReportIssueDialog } from './ReportIssueDialog';
import { PaymentStatementCard } from './PaymentStatementCard';
import { downloadPdf, errMsg, money } from './salaryApi';

const ConfirmCell = ({ p, onConfirm, onReport }) => {
  if (p.confirmed) return (
    <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2" data-testid={`receipt-btn-${p.id}`}
      onClick={() => downloadPdf(`/me/payment-confirmations/${p.kind}/${p.id}/receipt`, `receipt-${p.ack_reference}.pdf`).catch((e) => toast.error(errMsg(e)))}>
      <Download size={11} className="mr-1" />Receipt
    </Button>
  );
  return (
    <div className="inline-flex gap-1">
      <Button size="sm" className="h-7 text-[11px] px-2 bg-teal-600 hover:bg-teal-700 text-white" onClick={() => onConfirm(p)} data-testid={`confirm-btn-${p.id}`}>Confirm</Button>
      <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 text-rose-600" onClick={() => onReport(p)} data-testid={`report-btn-${p.id}`}>Report</Button>
    </div>
  );
};

const StatusBadges = ({ p }) => (
  <div className="flex flex-wrap gap-1 justify-center">
    <Badge variant="secondary" className="text-[10px]">Paid by Company</Badge>
    {p.confirmed
      ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700" data-testid={`confirmed-badge-${p.id}`}><CheckCircle2 size={10} className="mr-1" />Confirmed Received</Badge>
      : p.issue ? <Badge className="text-[10px] bg-rose-100 text-rose-700"><AlertTriangle size={10} className="mr-1" />Issue reported</Badge>
        : <Badge className="text-[10px] bg-amber-100 text-amber-700" data-testid={`unconfirmed-badge-${p.id}`}>Not yet confirmed</Badge>}
  </div>
);

export const PaymentConfirmationsPanel = ({ onChanged }) => {
  const [data, setData] = useState({ items: [] });
  const [pinStatus, setPinStatus] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const load = async () => {
    const [l, s] = await Promise.allSettled([api.get('/me/payment-confirmations'), api.get('/me/salary-pin/status')]);
    if (l.status === 'fulfilled') setData(l.value.data);
    if (s.status === 'fulfilled') setPinStatus(s.value.data);
  };
  useEffect(() => { load(); }, []);
  const done = () => { setConfirmFor(null); setReportFor(null); load(); onChanged?.(); };

  return (
    <div className="space-y-3" data-testid="payment-confirmations-panel">
      <SalaryPinCard status={pinStatus} onChanged={load} />
      {pinStatus && !pinStatus.id_on_record && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="no-id-on-record">
          Your HR record has no Employee ID / Iqama number yet, so confirmations are not possible. Please ask HR to add it.
        </p>
      )}
      <PaymentStatementCard />
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 text-xs text-muted-foreground flex flex-wrap gap-4 border-b">
            <span>Paid: <b className="text-foreground" data-testid="total-paid">{cur()} {money(data.total_paid)}</b></span>
            <span>Confirmed: <b className="text-foreground" data-testid="total-confirmed">{cur()} {money(data.total_confirmed)}</b></span>
            <span>Waiting for you: <b className="text-amber-700" data-testid="pending-count">{data.pending || 0}</b></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead><tr className="border-b text-xs text-muted-foreground">
                <th className="text-left p-3">Date</th><th className="text-left p-3">Type</th>
                <th className="text-left p-3 hidden sm:table-cell">Period / Ref</th><th className="text-right p-3">Amount</th>
                <th className="text-center p-3">Status</th><th className="text-right p-3">Action</th>
              </tr></thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={`${p.kind}-${p.id}`} className="border-b hover:bg-muted/40" data-testid={`payment-row-${p.id}`}>
                    <td className="p-3 font-mono text-xs">{p.date}</td>
                    <td className="p-3 text-xs">{p.type_label}</td>
                    <td className="p-3 text-xs hidden sm:table-cell">{p.period || '—'}<div className="text-muted-foreground font-mono">{p.reference}</div></td>
                    <td className="p-3 text-right font-medium tabular-nums">{cur()} {money(p.amount)}</td>
                    <td className="p-3"><StatusBadges p={p} /></td>
                    <td className="p-3 text-right"><ConfirmCell p={p} onConfirm={setConfirmFor} onReport={setReportFor} /></td>
                  </tr>
                ))}
                {data.items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No payments recorded yet</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <ConfirmPaymentDialog item={confirmFor} onClose={() => setConfirmFor(null)} onDone={done} />
      <ReportIssueDialog item={reportFor} onClose={() => setReportFor(null)} onDone={done} />
    </div>
  );
};
